import React from 'react';

import { translate } from '@vimeo/i18n-runtime';

import DownloadNotice from '../components/DownloadNotice';
import DownloadPanelDownloadButton from '../components/DownloadPanelDownloadButton';
import DownloadSourceFileRow from '../components/DownloadSourceFileRow';
import TextTracks from '../components/TextTracks';
import ThreeSixtyNotice from '../components/ThreeSixtyNotice';
import Throbber from '../components/Throbber';
import {
  DownloadButtonColumn,
  DownloadPanelStyled,
  FileConfigRow,
  FileDetails,
  FileTitle,
} from '../Styles';
import { FileDownload, TextTrack } from '../types';

type Props = {
  allowDownloads?: boolean;
  clipId: number;
  downloadConfig: FileDownload[];
  fetchColdSourceFile: (href?: string) => void;
  sourceFile?: FileDownload;
  textTracks?: TextTrack[];
  isOwnerOrAdmin: boolean;
  isDefrosting?: boolean;
  isSpatial?: boolean;
  isReview?: boolean;
};

type State = {
  coldStorageVisible: boolean;
  tooltipShow: boolean;
  coldStorageNotificationToggled?: boolean;
};

const tooltipStyle = {
  left: '30px',
};

const filterVideoFiles = (files: FileDownload[]): FileDownload[] => {
  return files
    .filter((file) => {
      return ['hd', 'sd', 'source', 'uhd'].includes(file.quality);
    })
    .sort((fileA, fileB) => {
      if (fileA.quality === 'source') {
        return 0;
      }
      return fileA.height - fileB.height;
    });
};

export class DownloadPanel extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      coldStorageVisible: false,
      tooltipShow: false,
    };

    this._handleIconMouseOver = this._handleIconMouseOver.bind(this);
    this._handleIconMouseOut = this._handleIconMouseOut.bind(this);
    this._handleFetchClick = this._handleFetchClick.bind(this);
    this._handleFetchConfirmation = this._handleFetchConfirmation.bind(this);
    this._handleColdStorageClose = this._handleColdStorageClose.bind(this);
  }

  _handleIconMouseOver() {
    this.setState({
      tooltipShow: true,
    });
  }

  _handleIconMouseOut() {
    this.setState({
      tooltipShow: false,
    });
  }

  _handleFetchClick(e: React.MouseEvent<HTMLElement, MouseEvent>) {
    e.preventDefault();
    this.setState({
      coldStorageNotificationToggled: true,
    });
  }

  _handleFetchConfirmation(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();
    const target = e.target as HTMLElement | null;
    if (target) {
      const href = target.getAttribute('href') || '';
      this.props.fetchColdSourceFile(href);
    }
  }

  _handleColdStorageClose(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();

    this.setState({
      coldStorageNotificationToggled: false,
    });
  }

  render() {
    const {
      clipId,
      downloadConfig,
      sourceFile,
      isSpatial,
      isOwnerOrAdmin,
      allowDownloads,
      textTracks,
      isReview,
      isDefrosting,
    } = this.props;

    const { coldStorageNotificationToggled, tooltipShow } = this.state;

    if (!downloadConfig || downloadConfig.length === 0) {
      return (
        <div id="download_panel">
          <Throbber />
        </div>
      );
    }

    const filteredFiles = filterVideoFiles(downloadConfig);

    const tooltipMessage = translate('This source file has been lovingly stored in our archive.');

    return (
      <DownloadPanelStyled>
        {isOwnerOrAdmin && !allowDownloads ? <DownloadNotice clipId={clipId} /> : null}
        {isSpatial ? <ThreeSixtyNotice /> : null}
        <div>
          {filteredFiles.map((file, i) => (
            <FileConfigRow key={`download_${i}`}>
              <FileTitle>
                {file.quality === 'source' ? translate('Original') : file.publicName}
              </FileTitle>
              <DownloadButtonColumn>
                <FileDetails>
                  {file.width} × {file.height} / {file.sizeShort}
                </FileDetails>
                <DownloadPanelDownloadButton
                  file={file}
                  isReview={isReview}
                  fetchFile={this._handleFetchClick}
                />
              </DownloadButtonColumn>
            </FileConfigRow>
          ))}
          {sourceFile ? (
            <DownloadSourceFileRow
              file={sourceFile}
              isDefrosting={isDefrosting}
              isReview={isReview}
              coldStorageNotificationToggled={coldStorageNotificationToggled}
              tooltipShow={tooltipShow}
              tooltipMessage={tooltipMessage}
              tooltipStyle={tooltipStyle}
              showTooltip={this._handleIconMouseOver}
              hideTooltip={this._handleIconMouseOut}
              fetchFile={this._handleFetchClick}
              fetchConfirmation={this._handleFetchConfirmation}
              coldStorageClose={this._handleColdStorageClose}
            />
          ) : null}
        </div>
        {textTracks && textTracks.length ? <TextTracks textTracks={textTracks} /> : null}
      </DownloadPanelStyled>
    );
  }
}
